import * as angular from 'angular';
import * as _ from 'lodash';

import { Identifier, KpiConfiguration, TagDateValue, TagDateValueForLogbookEntry } from '../../models';
import { GetAggregatedValueDto, GetAggregatedValuesDto, LastLogbookEntryRequestDto, ValueAcquisitionDto, ValueStateDto } from '../../models/DTO';
import { AggregationMode, IdentifierType } from '../../models/enum';
import { guidEmpty } from '../../common/constants';
import { IGroupService, IInputValuesService, ICalculatedValuesService } from './';
import { IDateRangeService } from '../general';
import { IRequestService } from '../request.service';
import { SeriesConfiguration } from '../../widgets';
import { ValueTypes } from '../../common';

interface GetValuesListDto {
    beginDate: string;
    endDate: string;
    logbookEntryId: number;
    list: ValueAcquisitionDto[];
}

export interface IValuesService {
    getValues(
        seriesConfigurationList: ValueAcquisitionDto[],
        beginDate?: string,
        endDate?: string,
        force?: boolean,
        canceller?: angular.IPromise<void>
    ): angular.IPromise<TagDateValue[]>;
    getCompressedValues(
        seriesConfigurationList: ValueAcquisitionDto[],
        beginDate?: string,
        endDate?: string,
        force?: boolean,
        canceller?: angular.IPromise<void>
    ): angular.IPromise<TagDateValue[]>;
    getValuesAndStatuses(identifierGuidList: string[], dateTime: string, force?: boolean): angular.IPromise<ValueStateDto[]>;
    getValuesWithMissing(
        seriesConfigurationList: { guid: string; value: string }[],
        beginDate?: string,
        endDate?: string,
        force?: boolean,
        canceller?: angular.IPromise<void>
    ): angular.IPromise<{ values: TagDateValue[]; missing: { key: string; value: { timeSpan: string; start: string; end: string }[] }[] }>;
    getAggregatedValueBetweenDates(kpis: KpiConfiguration[]): angular.IPromise<any>;
    getAggregatedValueBetweenDates(
        kpis: KpiConfiguration[],
        beginDate: string,
        endDate: string,
        aggregationMode: AggregationMode
    ): angular.IPromise<any>;
    getValueBetweenDates(
        beginDate: string,
        endDate: string,
        mode: AggregationMode,
        items: any[],
        force?: boolean
    ): angular.IPromise<any>;
    getLogbookSeriesValues(
        beginDate: string,
        endDate: string,
        series: string[],
        logbookEntryId: number
    ): angular.IPromise<TagDateValueForLogbookEntry[]>;
    getLastLogbookSeriesValues(
        lastEntryrequest : LastLogbookEntryRequestDto
    ): angular.IPromise<TagDateValueForLogbookEntry[]>;
}

export class ValuesService implements IValuesService {
    static serviceId = 'valuesService';

    private readonly _controllerAction = 'aggregatedValues/GetAggregatedValue';

    static $inject = [
        '$q',
        'groupService',
        'inputValuesService',
        'calculatedValuesService',
        'dateRangeService',
        'requestService'
    ];

    constructor(
        private $q: angular.IQService,
        private _groupService: IGroupService,
        private _inputValuesService: IInputValuesService,
        private _calculatedValuesService: ICalculatedValuesService,
        private _dateRangeService: IDateRangeService,
        private _requestService: IRequestService
    ) {
    }

    getLogbookSeriesValues(beginDate: string, endDate: string, series: string[], logbookEntryId: number): angular.IPromise<TagDateValueForLogbookEntry[]> {
        const url = 'values/logbook';
        const list: ValueAcquisitionDto[] = series.map(s => ({ guid: s, value: ValueTypes.average.key }));
        const data: GetValuesListDto = { beginDate, endDate, list, logbookEntryId };
        return this._requestService.postAtV1(url, data);
    }

    getLastLogbookSeriesValues( lastEntryrequest : LastLogbookEntryRequestDto): angular.IPromise<TagDateValueForLogbookEntry[]> {
        const url = 'values/lastLogbook';
        const beginDate = lastEntryrequest.beginDateTime;
        lastEntryrequest.mappedSeries = lastEntryrequest.series.map(s => ({ guid: s, value: ValueTypes.average.key }));
        return this._requestService.postAtV1(url, lastEntryrequest);
    }

    getValuesAndStatuses(identifierGuidList: string[], dateTime: string, force?: boolean): angular.IPromise<any> {
        const url = 'values/valuesAndStatuses';
        const data = {
            identifierGuidList: identifierGuidList,
            dateTime: dateTime
        };
        if (!!force) {
            this._requestService.cleanCachedPostValue(url, data);
        }
        return this._requestService.cachedPostAtV1(url, data);
    }

    getValues(
        seriesConfigurationList: ValueAcquisitionDto[],
        beginDate?: string,
        endDate?: string,
        force?: boolean,
        canceller?: angular.IPromise<void>
    ): angular.IPromise<TagDateValue[]> {
        if (!beginDate || !endDate) {
            if (this._dateRangeService.initialized()) {
                beginDate = this._dateRangeService.beginDate() as string;
                endDate = this._dateRangeService.endDate() as string;
            } else {
                return this.$q.reject({ message: 'No date provided' });
            }
        }
        const action = 'values/list';
        const config = {
            beginDate: beginDate,
            endDate: endDate,
            list: seriesConfigurationList
                .filter(s => s.guid !== guidEmpty)
                .map(s => {
                    return {
                        guid: s.guid,
                        value: s.value ? s.value : 'average'
                    };
                })
        };
        if (!!force) {
            this._requestService.cleanCachedPostValue(action, config);
        }
        return this.cachedPost(action, config, canceller);
    }

    getValuesWithMissing(
        seriesConfigurationList: SeriesConfiguration[],
        beginDate?: string,
        endDate?: string,
        force?: boolean,
        canceller?: angular.IPromise<void>
    ): angular.IPromise<any> {
        if (!beginDate || !endDate) {
            if (this._dateRangeService.initialized()) {
                beginDate = this._dateRangeService.beginDate() as string;
                endDate = this._dateRangeService.endDate() as string;
            } else {
                return this.$q.reject({ message: 'No date provided' });
            }
        }
        const action = 'values/listWithMissing';
        const configuration = {
            beginDate: beginDate,
            endDate: endDate,
            list: _.sortBy(seriesConfigurationList.map(s => {
                return {
                    guid: s.guid,
                    value: s.value ? s.value : 'average'
                };
            }), d => d.guid + d.value)
        };
        if (force) {
            this._requestService.cleanCachedPostValue(action, configuration);
        }
        return this.cachedPost(action, configuration, canceller);
    }

    getCompressedValues(
        seriesConfigurationList: ValueAcquisitionDto[],
        beginDate?: string,
        endDate?: string,
        force = false,
        canceller?: angular.IPromise<void>,
    ): angular.IPromise<TagDateValue[]> {
        const defered = this.$q.defer<TagDateValue[]>();
        if (!beginDate || !endDate) {
            if (this._dateRangeService.initialized()) {
                beginDate = this._dateRangeService.beginDate() as string;
                endDate = this._dateRangeService.endDate() as string;
            } else {
                defered.resolve([]);
            }
        }
        if (beginDate && endDate) {
            const configuration = {
                beginDate: beginDate,
                endDate: endDate,
                list: seriesConfigurationList.map(d => {
                    return { guid: d.guid, value: d.value };
                })
            };
            const action = 'values/compressedValueList';
            if (force) {
                this._requestService.cleanCachedPostValue(action, configuration);
            }
            this.cachedPost(action, configuration, canceller)
                .then(data => {
                    defered.resolve(data);
                })
                .catch(reason => defered.reject(reason));
        } else {
            defered.reject({ message: 'No dates provided' });
        }
        return defered.promise;
    }

    getCompressedValuesForOne(seriesConfiguration, beginDate, endDate): angular.IPromise<TagDateValue[]> {
        return this._requestService.getAtV1('values/compressedValue', {
            dto: {
                guid: seriesConfiguration.guid,
                value: seriesConfiguration.value ? seriesConfiguration.value : 'average',
                beginDate: beginDate,
                endDate: endDate
            }
        });
    }

    getAggregatedValueBetweenDates(kpis: KpiConfiguration[]): angular.IPromise<any>;
    getAggregatedValueBetweenDates(
        kpis: KpiConfiguration[],
        beginDate: string,
        endDate: string,
        aggregationMode: AggregationMode
    ): angular.IPromise<any>;
    getAggregatedValueBetweenDates(
        kpis: KpiConfiguration[],
        beginDate?: string,
        endDate?: string,
        aggregationMode?: AggregationMode
    ): angular.IPromise<any> {
        if (aggregationMode === null || aggregationMode === undefined || aggregationMode
            === AggregationMode.None) {
            aggregationMode = AggregationMode.Average;
        }
        beginDate = beginDate ? beginDate : this._dateRangeService.beginDate() as string;
        endDate = endDate ? endDate : this._dateRangeService.endDate() as string;
        const config: GetAggregatedValuesDto = {
            beginDateTime: beginDate,
            endDateTime: endDate,
            aggregationMode: aggregationMode,
            aggregationInterval: null,
            aggregationUnit: null,
            hasQuality: true,
            list: kpis.map(d => {
                return {
                    guid: d.guid,
                    value: d.value
                };
            })
        };
        return this.cachedPost('aggregatedValues/getBusinessKpis', config);
    }

    getValueBetweenDates(beginDate: string, endDate: string, mode: AggregationMode, items: any[], force?: boolean):
        angular.IPromise<any> {
        const configuration: GetAggregatedValueDto = {
            beginDateTime: beginDate,
            endDateTime: endDate,
            aggregationMode: mode,
            hasQuality: false,
            list: items.map(r => {
                return {
                    guid: r.guid,
                    value: (r && r.value) ? r.value : 'average'
                };
            })
        };
        if (force) {
            this._requestService.cleanCachedPostValue(this._controllerAction, configuration);
        }
        return this.cachedPost(this._controllerAction, configuration);
    }

    private getAggregatedValuesBetweenDatesByIdentifierType(
        identifier: Identifier,
        k: KpiConfiguration,
        beginDate: string,
        endDate: string,
        aggregationMode: AggregationMode
    ): angular.IPromise<any> {
        const dfd = this.$q.defer<any>();
        if (identifier.type === IdentifierType.Tag) {
            this._inputValuesService.getAggregatedValueBetweenDatesForTag(identifier.tagDefinitionGuid, k.value,
                beginDate, endDate, aggregationMode)
                .then(result => {
                    result.guid = identifier.guid;
                    dfd.resolve(result);
                })
                .catch((reason) => dfd.reject(reason));
            return dfd.promise;
        } else if (identifier.type === IdentifierType.CalculatedValue) {
            return this._calculatedValuesService.getAggregatedValueBetweenDates(k.guid, k.value, beginDate, endDate,
                aggregationMode);
        } else if (identifier.type === IdentifierType.Linked) {
            this._groupService.getIdentifier(identifier.linkedGuid)
                .then(newIdentifier => {
                    return this.getAggregatedValuesBetweenDatesByIdentifierType(newIdentifier, k, beginDate,
                        endDate, aggregationMode);
                })
                .then((result: any) => {
                    result.guid = identifier.guid;
                    dfd.resolve(result);
                })
                .catch((reason) => dfd.reject(reason));
            return dfd.promise;
        } else {
            const dfd = this.$q.defer<any>();
            dfd.reject('Unknwon identifier type');
            return dfd.promise;
        }
    }

    private cachedPost(controllerAction: string, configuration: any, cancel?: angular.IPromise<any>): angular.IPromise<any> {
        return this._requestService.cachedPostAtV1(controllerAction, configuration, undefined, false, cancel);
    }
}